/**
 * Compact template inventory for the CV studio.
 * Catalog looks are free to pick; prompt design is a separate Pro action.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getTemplateDisplayName } from '../utils/templateNames';
import './TemplateRail.css';

const TemplateRail = ({
  templates,
  selectedId,
  onSelect,
  onSeeAll,
  onDesignPrompt,
  designPromptOpen,
  onHideLooks,
  hideLooksLabel,
  lang,
  seeAllLabel,
  looksLabel,
  looksHint,
  looksBadge,
  designPromptLabel,
  designHint,
  proBadge,
  aiLookActive,
  aiLookLabel,
}) => {
  const [imageErrors, setImageErrors] = useState({});
  const selectedRef = useRef(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
    });
  }, [selectedId, aiLookActive]);

  return (
    <div className="template-rail">
      <div className="template-rail__catalog">
        <div className="template-rail__heading">
          <p className="template-rail__label">{looksLabel}</p>
          {looksBadge ? (
            <span className="template-rail__included">{looksBadge}</span>
          ) : null}
          {looksHint ? (
            <p className="template-rail__hint">{looksHint}</p>
          ) : null}
          {onHideLooks ? (
            <button
              type="button"
              className="template-rail__hide"
              onClick={onHideLooks}
              aria-expanded="true"
              title={hideLooksLabel}
            >
              <span className="template-rail__hide-icon" aria-hidden="true">▲</span>
              {hideLooksLabel}
            </button>
          ) : null}
        </div>
        <ul className="template-rail__list" aria-label={looksHint || looksLabel}>
          {aiLookActive ? (
            <li ref={aiLookActive ? selectedRef : undefined}>
              <button
                type="button"
                className="template-rail__item is-selected is-ai"
                aria-pressed="true"
                title={aiLookLabel}
                aria-label={aiLookLabel}
                onClick={onDesignPrompt}
              >
                <span className="template-rail__fallback" aria-hidden="true">
                  IA
                </span>
                <span className="template-rail__name">{aiLookLabel}</span>
              </button>
            </li>
          ) : null}
          {templates.map((template) => {
            const displayName = getTemplateDisplayName(template.name, lang);
            const selected = !aiLookActive && String(template.id) === String(selectedId);
            const showImage = Boolean(template.thumbnail_url) && !imageErrors[template.id];
            return (
              <li key={template.id} ref={selected ? selectedRef : undefined}>
                <button
                  type="button"
                  className={`template-rail__item${selected ? ' is-selected' : ''}`}
                  onClick={() => onSelect(template)}
                  aria-pressed={selected}
                  aria-label={displayName}
                  title={displayName}
                >
                  {showImage ? (
                    <img
                      className="template-rail__thumb"
                      src={template.thumbnail_url}
                      alt=""
                      width="72"
                      height="102"
                      loading="lazy"
                      decoding="async"
                      onError={() =>
                        setImageErrors((prev) => ({ ...prev, [template.id]: true }))
                      }
                    />
                  ) : (
                    <span className="template-rail__fallback" aria-hidden="true">
                      {displayName.slice(0, 1)}
                    </span>
                  )}
                  <span className="template-rail__name">{displayName}</span>
                </button>
              </li>
            );
          })}
          {onSeeAll ? (
            <li>
              <button
                type="button"
                className="template-rail__item template-rail__item--all"
                onClick={onSeeAll}
                title={seeAllLabel}
              >
                <span className="template-rail__fallback template-rail__fallback--all" aria-hidden="true">
                  ⋯
                </span>
                <span className="template-rail__name">{seeAllLabel}</span>
              </button>
            </li>
          ) : null}
        </ul>
      </div>
      {onDesignPrompt ? (
        <div className="template-rail__aside">
          <button
            type="button"
            className={`template-rail__ai${designPromptOpen ? ' is-open' : ''}`}
            onClick={onDesignPrompt}
            aria-expanded={Boolean(designPromptOpen)}
            aria-label={`${designPromptLabel}. ${proBadge}`}
          >
            <span className="template-rail__ai-badge">{proBadge}</span>
            <span className="template-rail__ai-copy">
              <span className="template-rail__ai-title">{designPromptLabel}</span>
              {designHint ? (
                <span className="template-rail__ai-hint">{designHint}</span>
              ) : null}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

TemplateRail.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  onSeeAll: PropTypes.func,
  onDesignPrompt: PropTypes.func,
  designPromptOpen: PropTypes.bool,
  onHideLooks: PropTypes.func,
  hideLooksLabel: PropTypes.string,
  lang: PropTypes.string,
  seeAllLabel: PropTypes.string.isRequired,
  looksLabel: PropTypes.string.isRequired,
  looksHint: PropTypes.string,
  looksBadge: PropTypes.string,
  designPromptLabel: PropTypes.string,
  designHint: PropTypes.string,
  proBadge: PropTypes.string,
  aiLookActive: PropTypes.bool,
  aiLookLabel: PropTypes.string,
};

TemplateRail.defaultProps = {
  selectedId: null,
  onSeeAll: undefined,
  onDesignPrompt: undefined,
  designPromptOpen: false,
  onHideLooks: undefined,
  hideLooksLabel: '',
  lang: 'es',
  looksHint: '',
  looksBadge: '',
  designPromptLabel: '',
  designHint: '',
  proBadge: 'PRO',
  aiLookActive: false,
  aiLookLabel: '',
};

export default TemplateRail;
